import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { AutoPolicy, RiskModel } from '@amfam/policy/models';
import {
  fromVehicleActions,
  NewVehicle,
  VehicleFacade,
  VehicleFeaturesModel,
  VehicleService
} from '@amfam/policy/vehicle/data-access';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions, routerQuery } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { DsModalService, LoadingSpinnerService, ValidationService } from '@amfam/ui-kit';

import {
  AnalyticsEventType,
  ReplaceVehicleQuickQuoteContext,
  ReplaceVehicleQuickQuoteOperation
} from '../+state/quick-quote-feature/quick-quote-enum';
import { QuickQuoteFeatureFacade } from '../+state/quick-quote-feature/quick-quote-feature.facade';

@Component({
  selector: 'ds-year-vin-lookup',
  templateUrl: './year-vin-lookup.component.html',
  styleUrls: ['./year-vin-lookup.component.scss'],
  standalone: false
})
export class YearVinLookupComponent implements OnInit, AfterViewInit {
  private stop$: Subject<void> = new Subject<void>();

  static readonly advancedGraceDays = '14';
  static readonly classicGraceDays = '30';
  exitPrimaryButtonText = this.copyService.getCopy(
    'policy.replaceVehicleExitFlowPrimaryButtonText'
  );

  @Input() policyNumber?: string;
  @Input() replaceVin?: string;

  yearVinForm: UntypedFormGroup;

  hasError$ = this.quickQuoteFeatureFacade.vehicleError$;
  errorMessage$ = this.quickQuoteFeatureFacade.vinYearErrorMessage$;
  years$ = this.quickQuoteFeatureFacade.vehicleYears$;
  sameVINErrorMessage: string;

  vinContent = this.copyService.getCopy('policy.findVIN');
  findVinFlowModalId = this.modalService.createModalId('findVinFlowModalId');
  public exitFlowModalId = this.modalService.createModalId('exitFlowModalId');
  public exitFlowConfimationText = this.copyService.getCopy(
    'policy.replaceVehicleExitFlowConfimationText'
  );

  // Form elements
  yearCtrl: AbstractControl;
  vinCtrl: AbstractControl;

  // Grace period
  displayPolicyGracePeriod = false;
  gracePeriodDays = YearVinLookupComponent.advancedGraceDays;
  policy: AutoPolicy;

  isSameVin = false;
  private replaceVehicleRisks$: Observable<RiskModel[]> =
    this.quickQuoteFeatureFacade.replaceVehicleRisks$;

  public activeAgents$ = this.quickQuoteFeatureFacade.agents$;
  public getAdvanceAutoPolicies$ = this.quickQuoteFeatureFacade.advancedPolicies$;
  public userState$ = this.quickQuoteFeatureFacade.userState$;
  public loading$ = this.quickQuoteFeatureFacade.isVehicleLoading$;
  private vehicleId: string;
  showMsrp$: Observable<boolean>;
  vehicleDetails: { newVehicle: NewVehicle; features: VehicleFeaturesModel[] };

  errors: Map<string, string> = new Map<string, string>([
    ['400003', "Vehicle Year doesn't match VIN"],
    ['400004', "Vehicle Year doesn't match VIN"],
    [
      '*',
      'We’re not currently able to give a quote for this vehicle. To get a quote, please contact your agent.'
    ]
  ]);

  errorMessage: string;
  enabledMsrp = this.featureflagService.isEnabled('msrp');

  constructor(
    private store: Store<any>,
    private formBuilder: UntypedFormBuilder,
    public roleService: ImpersonateRolesService,
    public spinner: LoadingSpinnerService,
    public copyService: CopyService,
    public quickQuoteFeatureFacade: QuickQuoteFeatureFacade,
    private modalService: DsModalService,
    private route: ActivatedRoute,
    private vehicleService: VehicleService,
    private featureflagService: FeatureFlagService,
    private analyticsFacade: AnalyticsFacade,
    private vehicleFacade: VehicleFacade
  ) {}

  ngOnInit() {
    this.vehicleId = this.route.snapshot.paramMap.get('id');
    this.buildForm();
    if (this.vehicleId) this.quickQuoteFeatureFacade.setVehicleId(this.vehicleId);

    this.showMsrp$ = combineLatest([this.vinCtrl.valueChanges, this.yearCtrl.valueChanges]).pipe(
      tap(() => (this.errorMessage = '')),
      filter(([vin, year]) => !!vin && !!year && this.vinCtrl.valid && this.yearCtrl.valid),
      switchMap(([vin, year]) =>
        this.vehicleService
          .getVehicleByVinAndYear({
            year: year,
            vin: vin
          })
          .pipe(
            tap(vehicle => {
              this.errorMessage = null;
              this.vehicleDetails = vehicle;
            }),
            map(vehicle => (!!vehicle ? 1 : 0)),
            catchError(err => {
              this.setError(err, year);
              this.vehicleDetails = null;
              return of(-1);
            })
          )
      ),
      map(success => {
        const year = this.yearCtrl.value;
        return success === 0 && year >= new Date().getFullYear();
      }),
      catchError(err => {
        this.setError(err, this.yearCtrl?.value);
        this.vehicleDetails = null;
        return of(false);
      })
    );

    this.showMsrp$
      .pipe(
        takeUntil(this.stop$),
        tap(showMsrp => {
          if (showMsrp)
            this.analyticsFacade.trackEvent({
              event: 'vin_not_found_start',
              form_name: 'Vin Not Found',
              method: 'Replace Vehicle'
            });
        })
      )
      .subscribe();
  }

  ngAfterViewInit() {
    this.roleService
      .isAuthorized('replacevehicle_vinyear')
      .pipe(take(1))
      .subscribe(isAuthorized => {
        if (!isAuthorized) {
          this.yearVinForm.disable();
        }
      });
  }

  verifyVin(vin: string) {
    if (vin.length === 17) {
      this.replaceVehicleRisks$.pipe(take(1)).subscribe(vehicle => {
        const matchingVehicle = vehicle.find(vehicleVINRisks => {
          return vehicleVINRisks?.vin === vin.toUpperCase();
        });
        if (matchingVehicle) {
          this.isSameVin = true;
          this.sameVINErrorMessage = this.copyService.get(
            'policy',
            'sameVINReplaceVehicleErrorMessage'
          );
        } else {
          this.isSameVin = false;
          this.sameVINErrorMessage = '';
        }
      });
    }
  }

  private openModal = async () => {
    this.modalService.open(this.findVinFlowModalId);
  };

  findVIN(e) {
    e.preventDefault();
    this.openModal().then(() => {
      this.quickQuoteFeatureFacade.sendAnalytics(AnalyticsEventType.REPLACE_VEHICLE_FIND_VIN);
    });
  }

  buildForm() {
    const formGroup = {
      year: ['', Validators.required],
      vin: [
        '',
        Validators.compose([
          ValidationService.vinFormatValidator,
          ValidationService.vinRequiredValidator
        ])
      ]
    };
    this.yearVinForm = this.formBuilder.group(formGroup);
    this.yearCtrl = this.yearVinForm.controls['year'];
    this.vinCtrl = this.yearVinForm.controls['vin'];
  }

  toggleGracePeriod() {
    this.displayPolicyGracePeriod = !this.displayPolicyGracePeriod;
  }

  goBackOneStep() {
    this.store.dispatch(fromRouterActions.Back());
  }

  exitEvent() {
    this.modalService.open(this.exitFlowModalId);
  }

  stayFromExitModal() {}

  navigateToOverview() {
    this.store
      .pipe(select(routerQuery.getRouterCurrentStateUrl), take(1))
      .subscribe((url: string) => {
        this.store.dispatch(
          fromRouterActions.Go({
            path: ['overview']
          })
        );
      });
  }

  onVehicleDetailsLoaded(vehicleDetails) {
    if (vehicleDetails?.newVehicle?.series === 'other') vehicleDetails.newVehicle.series = '';
    this.vehicleDetails = vehicleDetails;
  }

  private dispatchValidYearVin() {
    this.analyticsFacade.trackEvent({
      event: 'vin_not_found_complete',
      form_name: 'Vin Not Found',
      method: 'Replace Vehicle'
    });
    this.store.dispatch(
      fromVehicleActions.LookupVinYearSuccess({
        ...this.vehicleDetails,
        criteria: { ...this.vehicleDetails.newVehicle }
      })
    );
  }

  submit() {
    this.quickQuoteFeatureFacade.setContext(ReplaceVehicleQuickQuoteContext.REPLACE_VEHICLE);
    if (this.enabledMsrp) {
      const make = _get(this.yearVinForm, 'controls.make.value');
      const model = _get(this.yearVinForm, 'controls.model.value');
      const vin = _get(this.yearVinForm, 'controls.vin.value');
      const series = _get(this.yearVinForm, 'controls.series.value');
      const msrp = this.vehicleDetails?.newVehicle?.msrp;

      if (make === 'other' || model === 'other' || !msrp) {
        this.quickQuoteFeatureFacade.setOperation(
          ReplaceVehicleQuickQuoteOperation.REPLACE_VEHICLE_HARDSTOP_OTHER_SEND_TO_AGENT
        );
        // TO DO Set the criteria in the store
        this.vehicleFacade.setCriteria({ vin: vin, series: series });

        this.quickQuoteFeatureFacade.sendToAgentReplaceVehicle();
        this.quickQuoteFeatureFacade.sendAnalytics(
          AnalyticsEventType.REPLACE_VEHICLE_HARD_STOP_OTHER_SEND_TO_AGENT
        );
      } else {
        this.dispatchValidYearVin();
      }
    } else this.dispatchValidYearVin();
  }

  private setError(err: any, year: number) {
    this.errorMessage = null;
    const errors = err?.status?.errors;
    if (year < new Date().getFullYear() || !this.enabledMsrp) {
      if (errors?.length > 0 && this.errors.has(String(errors[0].code))) {
        this.errorMessage = this.errors.get(String(errors[0].code));
        return;
      }
      this.errorMessage = this.errors.get('*');
    }
  }
}
